import {
  Component,
  ChangeDetectionStrategy,
  inject,
  computed,
  signal,
  Input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ResumeDataService } from '../../services/resume-data.service';
import { WidgetsModule } from '../../../../../shared/widgets.module';
import { ChangeLogEntry, ChangeLogSection } from '../../resume.model';

type SectionFilter = ChangeLogSection | 'all';

@Component({
    selector: 'app-change-log-tab',
    imports: [CommonModule, WidgetsModule],
    templateUrl: './change-log-tab.component.html',
    styleUrls: ['./change-log-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChangeLogTabComponent {
  private readonly resumeData = inject(ResumeDataService);

  readonly loading   = computed(() => this.resumeData.changeLogLoading());
  readonly changeLog = computed(() => this.resumeData.changeLogTabData());

  /** Accordion: index of the currently open entry; -1 = all collapsed */
  readonly expandedIndex = signal<number>(0);

  /** Active section filter */
  readonly activeFilter = signal<SectionFilter>('all');

  readonly sectionFilters: { value: SectionFilter; label: string }[] = [
    { value: 'all',        label: 'All'          },
    { value: 'client',     label: 'Client info'  },
    { value: 'agreement',  label: 'Agreement'    },
    { value: 'collection', label: 'Collection'   },
  ];

  /**
   * Re-fetches every time the tab becomes the active tab.
   * Uses the @Input setter pattern so we react on every activation,
   * not just the first visit.
   */
  @Input() set isActive(active: boolean) {
    if (active && this.resumeData.clientID() && !this.resumeData.changeLogLoading()) {
      this.expandedIndex.set(0); // always open the most recent entry
      this.resumeData.fetchChangeLogData().catch(() => {});
    }
  }

  /** Sections shown after applying the active filter */
  readonly filteredSections = computed<ChangeLogEntry[]>(() => {
    const data = this.changeLog();
    if (!data) return [];
    const sections = data.sections ?? [];
    const filter = this.activeFilter();
    return filter === 'all' ? sections : sections.filter(s => s.section === filter);
  });

  toggleEntry(index: number): void {
    this.expandedIndex.set(this.expandedIndex() === index ? -1 : index);
  }

  setFilter(value: SectionFilter): void {
    this.activeFilter.set(value);
    this.expandedIndex.set(0); // reopen first entry when filter changes
  }

  getSectionBadgeClass(section: ChangeLogSection): string {
    const map: Record<ChangeLogSection, string> = {
      client:     'badge-section badge-client',
      agreement:  'badge-section badge-agreement',
      collection: 'badge-section badge-collection',
    };
    return map[section] ?? 'badge-section';
  }

  getSectionLabel(section: ChangeLogSection): string {
    const map: Record<ChangeLogSection, string> = {
      client:     'Client info',
      agreement:  'Agreement',
      collection: 'Collection',
    };
    return map[section] ?? section;
  }

  /** Maps raw DB field names to human-readable labels */
  formatFieldName(field: string): string {
    const overrides: Record<string, string> = {
      reconciliation_type:  'Reconciliation Type',
      payment_method:       'Payment Method',
      payment_method_id:    'Payment Method',
      flat_price:           'Flat Price',
      per_percentage:       'Per Percentage',
      per_gallon:           'Per Gallon',
      per_pounds:           'Per Pounds',
      payment_to:           'Payment To',
      name_client:          'Client Name',
      name_project:         'Project Name',
      contact_name:         'Contact Name',
      business_type:        'Business Type',
      typeAgreement:        'Agreement Type',
      sales_person:         'Sales Person',
      zip_code:             'Zip Code',
      ident_GUI:            'Client ID',
      send_collection_receipt: 'Send Collection Receipt',
    };
    return overrides[field] ?? field
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  /** Maps raw DB values to human-readable labels, field-aware */
  formatValue(value: any, field?: string): string {
    if (value === null || value === undefined) return '—';

    // ── Boolean (real or string "true"/"false") ──────────────────────────────
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value === 'true')  return 'Yes';
    if (value === 'false') return 'No';

    // ── Arrays ───────────────────────────────────────────────────────────────
    if (Array.isArray(value)) return value.length ? value.join(', ') : '—';

    const str = String(value).trim();

    // ── Empty string ─────────────────────────────────────────────────────────
    if (str === '') return '—';

    // ── MongoDB ObjectId (24-char hex) — replace with readable note ──────────
    if (/^[a-f0-9]{24}$/i.test(str)) {
      return '(reference)';
    }

    // ── Reconciliation type enum ─────────────────────────────────────────────
    if (field === 'reconciliation_type') {
      const typeMap: Record<string, string> = {
        flat_price:     'Flat Rate',
        Per_gallon:     'By Gallons',
        Per_pounds:     'By Pounds',
        Per_percentage: 'By Percentage',
      };
      return typeMap[str] ?? str;
    }

    // ── Generic: return as-is ────────────────────────────────────────────────
    return str;
  }
}
